/**
 * When defining translations in VueI18n, different plural variants are provided:
 * - zero objects
 * - one object
 * - multiple objects
 *
 * This function picks one of three (or four) variants that can be provided for Croatian.
 *
 * Three variant case:
 * 1. zero, <nula prijatelja>
 * 2. one or numbers ending with one (excluding teens), <1 prijatelj>
 * 3. rest <2 prijatelja, 19 prijatelja>
 *
 * Four variant case:
 * 1. zero, <nula torbi>
 * 2. one or numbers ending with one (excluding teens), <1 torba>
 * 3. numbers ending with 2, 3 or 4 (excluding teens) <2 torbe>
 * 4. rest <19 torbi>
 */

export const PluralizationVariant = {
    Zero: 0,
    One: 1,
    Two: 2,
    Three: 3,
} as const;

export type PluralizationVariant = typeof PluralizationVariant[keyof typeof PluralizationVariant];
export type PluralizationFunction = (n: number, numberOfAvailableTranslations: number) => PluralizationVariant;

// eslint-disable-next-line sonarjs/cognitive-complexity
export const selectPluralTranslation: PluralizationFunction = (n, numberOfAvailableTranslations) => {
    if (n === 0) {
        return PluralizationVariant.Zero;
    }

    const teen = n > 10 && n < 20;
    const endsWithOne = n % 10 === 1;

    if (numberOfAvailableTranslations === 2) {
        return !teen && endsWithOne
            ? PluralizationVariant.Zero
            : PluralizationVariant.One;
    } else if (numberOfAvailableTranslations < 4) {
        return !teen && endsWithOne
            ? PluralizationVariant.One
            : PluralizationVariant.Two;
    } else {
        if (!teen && endsWithOne) {
            return PluralizationVariant.One;
        }

        const endsWithTwoThreeFour = n % 10 >= 2 && n % 10 <= 4;
        if (!teen && endsWithTwoThreeFour) {
            return PluralizationVariant.Two;
        }

        return PluralizationVariant.Three;
    }
};
